import { Node, mergeAttributes } from '@tiptap/core';

// A node that wraps the text nodes with a header/title element
// * Doesn't have any marks or attributes
// * Shouldn't affect the layout of the text
// * Should not be deletable / be removed when the text is deleted

export type Level = 1 | 2 | 3 | 4 | 5 | 6;

export interface HeaderOptions {
  levels: Level[];
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    header: {
      /**
       * Set a heading node
       */
      setHeading: () => ReturnType;
      /**
       * Unset a heading node
       * */
      unsetHeading: () => ReturnType;
      setHeadingLevel: (attributes: { level: Level }) => ReturnType;
    };
  }
}

export const CustomHeader = Node.create<HeaderOptions>({
  name: 'header',
  group: 'block',
  content: 'block+',
  defining: true,

  addOptions() {
    return {
      levels: [1, 2, 3, 4, 5, 6],
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      level: { default: 1 },
    };
  },

  parseHTML() {
    return this.options.levels.map((level: Level) => {
      return { tag: `h${level}`, attrs: { level } };
    });
  },

  renderHTML({ HTMLAttributes, node }) {
    const hasLevel = this.options.levels.includes(node.attrs.level);
    const level = hasLevel ? node.attrs.level : this.options.levels[0];
    const tag = `h${level}`;

    // no need to add this attribute to the element
    delete HTMLAttributes.level;

    return [tag, mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setHeading:
        () =>
        ({ commands }) => {
          return commands.wrapIn(this.name);
        },
      setHeadingLevel:
        ({ level }) =>
        ({ commands }) => {
          if (!level) return false;
          return commands.updateAttributes(this.name, { level });
        },
      unsetHeading:
        () =>
        ({ commands }) => {
          return commands.lift(this.name);
        },
    };
  },
});
